import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './NewsPost.css';

// Icons
import { BiSolidQuoteLeft, BiSolidQuoteRight } from 'react-icons/bi';
import { CiShare2 } from 'react-icons/ci';
import { FaExclamationCircle, FaLinkedin, FaInstagram } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { FiLink } from 'react-icons/fi';

// Assets
import temp_image from '../BlogFolder/img/blog-banner.png';

/* ------------------------------------------------------------------ */
/* Types (mirrors the DRF serializers)                                 */
/* ------------------------------------------------------------------ */

interface ParagraphBlock {
    contentID: number;
    block_type: 'paragraph';
    text: string;
    order: number;
}

interface QuoteBlock {
    contentID: number;
    block_type: 'quote';
    text: string;
    quote_author: string;
    order: number;
}

interface ImageBlock {
    contentID: number;
    block_type: 'image';
    image: string;
    image_title: string | null;
    image_description: string | null;
    order: number;
}

interface VideoBlock {
    contentID: number;
    block_type: 'video';
    video_url: string;
    image_title: string | null;
    image_description: string | null;
    order: number;
}

interface EntityBlock {
    contentID: number;
    block_type: 'entity';
    entity: string;
    entity_about: string;
    entity_image: string | null;
    entity_website: string | null;
    entity_email: string | null;
    order: number;
}

type ContentBlock = ParagraphBlock | QuoteBlock | ImageBlock | VideoBlock | EntityBlock;

interface NewsArticle {
    postID: number;
    link_name: string;
    title: string;
    sub_title: string | null;
    kicker: string;
    summary: string;
    date: string;
    mainImage: string; // resolved by NewsArticleSerializer.to_representation
    content_blocks: ContentBlock[];
}

// Shape returned by the merged /api/posts/?type=news feed (PostViewSet.list).
// This mixes two kinds of items under one shape:
//  - content_type: 'post'    → external link-out (href is the destination, link_name is null)
//  - content_type: 'article' → internal NewsArticle (link_name is set, href is null;
//                               we build the /news/:link_name URL ourselves below)
interface FeedItem {
    id: string;
    content_type: 'post' | 'article';
    title: string;
    kicker: string | null;
    sub_title: string | null;
    summary: string | null;
    href: string | null;
    link_name: string | null;
    new_tab: boolean;
    date: string;
    datetime: string;
    image_url: string | null;
    is_news: boolean;
    is_multimedia: boolean;
}

interface PaginatedResponse<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const truncateSummary = (summary: string) =>
    summary.length > 150 ? summary.slice(0, 150) + "..." : summary;

const handleCopyEmail = (email: string) => {
    navigator.clipboard
        .writeText(email)
        .then(() => toast.success("Email copied to clipboard!"))
        .catch(() => toast.error("Could not copy email"));
};

const formatDate = (date: string) =>
    date
        ? new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
        : '';

function getVideoInfo(url: string) {
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)(?:\/([\w-]+))?/);
    if (vimeoMatch) {
        return {
            type: 'vimeo',
            embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}${vimeoMatch[2] ? `?h=${vimeoMatch[2]}` : ''
                }`,
        };
    }

    const youtubeMatch = url.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^?&/]+)/
    );

    if (youtubeMatch) {
        const videoId = youtubeMatch[1];
        const parsed = new URL(url);
        const t = parsed.searchParams.get('t');
        let start = '';

        if (t) {
            let seconds = 0;
            if (/^\d+$/.test(t)) {
                seconds = Number(t);
            } else if (/^\d+s$/.test(t)) {
                seconds = Number(t.replace('s', ''));
            } else {
                const m = t.match(/(?:(\d+)m)?(?:(\d+)s)?/);
                if (m) seconds = Number(m[1] || 0) * 60 + Number(m[2] || 0);
            }
            if (seconds > 0) start = `?start=${seconds}`;
        }

        return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}${start}` };
    }

    return null;
}

// Helper: apply bold, italics, underline, title
function applyFormatting(
    text: string,
    keyPrefix = '',
    isFirstElement = false
) {
    const formatRegex = /(b|i|u|t)\[([^\]]+)\]/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = formatRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        const type = match[1];
        const content = match[2];
        let element: JSX.Element | null = null;

        switch (type) {
            case 'b':
                element = <strong key={`${keyPrefix}-b-${match.index}`}>{content}</strong>;
                break;
            case 'i':
                element = <em key={`${keyPrefix}-i-${match.index}`}>{content}</em>;
                break;
            case 'u':
                element = <u key={`${keyPrefix}-u-${match.index}`}>{content}</u>;
                break;
            case 't':
                element = (
                    <h2
                        className={`title-from-admin ${isFirstElement && match.index === 0 ? 'title-from-admin-first' : ''}`}
                        key={`${keyPrefix}-t-${match.index}`}
                    >
                        {content}
                    </h2>
                );
                break;
            default:
                element = null;
        }

        if (element) parts.push(element);
        lastIndex = formatRegex.lastIndex;
    }

    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
}

// Handles hyperlinks, then hands remaining text to applyFormatting
function renderWithFormatting(text: string, keyPrefix = '', isFirstElement = false) {
    const multiWordLinkRegex = /\[([^\]]+)\]\*\((https?:\/\/[^\s)]+)\)/g;
    const singleWordLinkRegex = /(\S+)\*\((https?:\/\/[^\s)]+)\)/g;

    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;

    // Only the very first applyFormatting call actually sits at the start
    // of the original text — every later segment comes after some link.
    let firstSegmentConsumed = !isFirstElement;
    const applyFmt = (segment: string, kp: string) => {
        const useFirst = !firstSegmentConsumed;
        firstSegmentConsumed = true;
        return applyFormatting(segment, kp, useFirst);
    };

    while ((match = multiWordLinkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(...applyFmt(text.slice(lastIndex, match.index), keyPrefix));
        }

        const displayText = match[1];
        const url = match[2];

        parts.push(
            <a
                key={`${keyPrefix}-multi-link-${match.index}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="news-inline-link"
            >
                {displayText}
            </a>
        );

        lastIndex = multiWordLinkRegex.lastIndex;
    }

    const remainingText = text.slice(lastIndex);
    const innerParts: (string | JSX.Element)[] = [];
    let innerLastIndex = 0;
    let innerMatch;

    while ((innerMatch = singleWordLinkRegex.exec(remainingText)) !== null) {
        if (innerMatch.index > innerLastIndex) {
            innerParts.push(
                ...applyFmt(remainingText.slice(innerLastIndex, innerMatch.index), keyPrefix)
            );
        }

        const word = innerMatch[1];
        const url = innerMatch[2];

        innerParts.push(
            <a
                key={`${keyPrefix}-link-${innerMatch.index}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="news-inline-link"
            >
                {word}
            </a>
        );

        innerLastIndex = singleWordLinkRegex.lastIndex;
    }

    if (innerLastIndex < remainingText.length) {
        innerParts.push(...applyFmt(remainingText.slice(innerLastIndex), keyPrefix));
    }

    parts.push(...innerParts);
    return parts;
}

/* ------------------------------------------------------------------ */
/* Share menu                                                          */
/* ------------------------------------------------------------------ */

interface ShareMenuProps {
    open: boolean;
    onClose: () => void;
    url: string;
    title: string;
    anchorRef: React.RefObject<HTMLElement>;
    align?: 'left' | 'right';
}

// Rendered through a portal into document.body and positioned with
// `position: fixed` off the trigger's bounding rect. This is what lets
// the menu float above ancestors like .news-hero-section or
// .news-suggestion-card, which use `overflow: hidden` for image
// cropping and would otherwise clip an absolutely-positioned popover.
const ShareMenu = ({ open, onClose, url, title, anchorRef, align = 'right' }: ShareMenuProps) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [rect, setRect] = useState<DOMRect | null>(null);

    useEffect(() => {
        if (!open || !anchorRef.current) return;

        const updatePosition = () => {
            if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
        };

        updatePosition();

        // Keep the menu glued to its trigger if the page scrolls or resizes
        // while it's open, rather than leaving it floating in a stale spot.
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, anchorRef]);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const clickedMenu = menuRef.current && menuRef.current.contains(target);
            const clickedAnchor = anchorRef.current && anchorRef.current.contains(target);
            if (!clickedMenu && !clickedAnchor) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, onClose, anchorRef]);

    if (!open || !rect) return null;

    const copyLink = () =>
        navigator.clipboard
            .writeText(url)
            .then(() => toast.success('Link copied to clipboard!'))
            .catch(() => toast.error('Could not copy link'));

    const handleCopyLink = (e: React.MouseEvent) => {
        e.stopPropagation();
        copyLink();
        onClose();
    };

    const handleLinkedIn = (e: React.MouseEvent) => {
        e.stopPropagation();
        window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
            '_blank',
            'noopener,noreferrer'
        );
        onClose();
    };

    const handleX = (e: React.MouseEvent) => {
        e.stopPropagation();
        window.open(
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
                title
            )}`,
            '_blank',
            'noopener,noreferrer'
        );
        onClose();
    };

    const handleInstagram = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Instagram has no web share intent for links, so copy the link
        // and let the user paste it into a story / bio / DM themselves.
        navigator.clipboard
            .writeText(url)
            .then(() => toast.success('Link copied — paste it into your Instagram story or bio!'))
            .catch(() => toast.error('Could not copy link'));
        onClose();
    };

    const menuStyle: React.CSSProperties = {
        position: 'fixed',
        top: rect.bottom + 8,
        ...(align === 'right'
            ? { right: Math.max(8, window.innerWidth - rect.right) }
            : { left: Math.max(8, rect.left) }),
    };

    return ReactDOM.createPortal(
        <div
            className="news-share-menu"
            style={menuStyle}
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
        >
            <button className="news-share-menu-item" onClick={handleCopyLink}>
                <FiLink /> Copy link
            </button>
            <button className="news-share-menu-item" onClick={handleLinkedIn}>
                <FaLinkedin /> LinkedIn
            </button>
            <button className="news-share-menu-item" onClick={handleX}>
                <FaXTwitter /> X
            </button>
            <button className="news-share-menu-item" onClick={handleInstagram}>
                <FaInstagram /> Instagram
            </button>
        </div>,
        document.body
    );
};

/* ------------------------------------------------------------------ */
/* Skeleton loading state                                              */
/* ------------------------------------------------------------------ */

const NewsPostSkeleton = () => (
    <div className="news-page-container">
        <div className="news-hero-section news-skeleton-hero">
            <div className="news-hero-wrapper">
                <div className="skeleton-block skeleton-kicker" />
                <div className="skeleton-block skeleton-title" />
                <div className="skeleton-block skeleton-title skeleton-title-short" />
                <div className="skeleton-block skeleton-subtitle" />
                <div className="news-byline-row">
                    <div className="skeleton-block skeleton-date" />
                </div>
            </div>
        </div>

        <div className="news-article-body">
            <div className="skeleton-block skeleton-line skeleton-line-full" />
            <div className="skeleton-block skeleton-line skeleton-line-full" />
            <div className="skeleton-block skeleton-line skeleton-line-90" />
            <div className="skeleton-block skeleton-line skeleton-line-full" />
            <div className="skeleton-block skeleton-line skeleton-line-70" />

            <div className="skeleton-block skeleton-image" />

            <div className="skeleton-block skeleton-line skeleton-line-full" />
            <div className="skeleton-block skeleton-line skeleton-line-95" />
            <div className="skeleton-block skeleton-line skeleton-line-60" />
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/* More-news card                                                      */
/* ------------------------------------------------------------------ */

const MoreNewsCard = ({ item }: { item: FeedItem }) => {
    const navigate = useNavigate();
    const [shareOpen, setShareOpen] = useState(false);
    const shareBtnRef = useRef<HTMLDivElement>(null);

    const isArticle = item.content_type === 'article';
    const internalHref = isArticle ? `/news/${item.link_name}` : null;
    const shareUrl = isArticle
        ? `${window.location.origin}${internalHref}`
        : item.href || window.location.href;

    const handleClick = () => {
        if (isArticle && internalHref) {
            navigate(internalHref);
            return;
        }
        if (item.href) {
            if (item.new_tab) {
                window.open(item.href, '_blank', 'noopener,noreferrer');
            } else {
                window.location.href = item.href;
            }
        }
    };

    return (
        <div className="news-suggestion-card" onClick={handleClick}>
            <div className="news-card-img-wrap">
                <img src={item.image_url || ''} alt="" className="news-card-img" />
                {item.kicker && <div className="news-card-kicker">{item.kicker}</div>}
                <div className="news-card-share-wrapper">
                    <div
                        ref={shareBtnRef}
                        className="news-card-share"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShareOpen((v) => !v);
                        }}
                    >
                        <CiShare2 className="share-icon" />
                    </div>
                    <ShareMenu
                        open={shareOpen}
                        onClose={() => setShareOpen(false)}
                        url={shareUrl}
                        title={item.title}
                        anchorRef={shareBtnRef}
                        align="right"
                    />
                </div>
            </div>

            <div className="news-card-body">
                <div>
                    <h3>{item.title}</h3>
                    {item.summary && <p className="news-card-summary">{truncateSummary(item.summary)}</p>}
                </div>

                <div className="news-card-meta">
                    <span>{formatDate(item.date)}</span>
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

const NewsPost = () => {
    const { link_name } = useParams<{ link_name?: string }>();

    const [article, setArticle] = useState<NewsArticle | null>(null);
    const [articleLoading, setArticleLoading] = useState(true);
    const [articleNotFound, setArticleNotFound] = useState(false);

    const [recommended, setRecommended] = useState<FeedItem[]>([]);
    const [heroShareOpen, setHeroShareOpen] = useState(false);
    const heroShareBtnRef = useRef<HTMLButtonElement>(null);

    // Fetch the article itself
    useEffect(() => {
        if (!link_name) {
            setArticleNotFound(true);
            setArticleLoading(false);
            return;
        }

        setArticleLoading(true);
        setArticleNotFound(false);

        const fetchArticle = async () => {
            try {
                const resp = await fetch(`/api/news/by-slug/${link_name}/`, {
                    credentials: "same-origin",
                });

                if (resp.status === 404) {
                    setArticleNotFound(true);
                    return;
                }

                if (!resp.ok) throw new Error('Failed to fetch article');

                const data: NewsArticle = await resp.json();
                setArticle(data);
            } catch (err) {
                console.error(err);
                toast.error('Error loading article');
                setArticleNotFound(true);
            } finally {
                setArticleLoading(false);
            }
        };

        fetchArticle();
    }, [link_name]);

    // Fetch "More News" from the merged feed (news-type posts + articles),
    // not just other articles — gives a richer mix of recommendations.
    useEffect(() => {
        const fetchMore = async () => {
            try {
                const resp = await fetch('/api/posts/?type=news&page_size=4', {
                    credentials: "same-origin",
                });
                if (!resp.ok) throw new Error('Failed to fetch news list');

                const data: PaginatedResponse<FeedItem> | FeedItem[] = await resp.json();

                const results = Array.isArray(data) ? data : data.results;
                results.filter(
                    item => !(item.content_type === 'article' && item.link_name === link_name)
                )
                setRecommended(
                    results.filter(
                        (item) => !(item.content_type === 'article' && item.link_name === link_name)
                    )
                );
            } catch (err) {
                console.error(err);
                toast.error('Error loading more news');
            }
        };

        fetchMore();
    }, [link_name]);

    if (articleLoading) {
        return <NewsPostSkeleton />;
    }

    if (articleNotFound || !article) {
        return (
            <div className="blog-post-hero-section not-blog">
                <img className="contact-hero-image" alt="hero background" src={temp_image} />
                <div className="dark-overlay"></div>
                <div className="overall-blog-post-wrapper">
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <FaExclamationCircle /> Article Not Found
                    </h1>
                </div>
            </div>
        );
    }

    const heroShareUrl = `${window.location.origin}/news/${article.link_name}`;

    return (
        <div className="news-page-container">
            <ToastContainer />

            {/* Hero */}
            <div className="news-hero-section">
                <img className="news-hero-image" alt="" src={article.mainImage} />
                <div className="news-dark-overlay" />

                <div className="news-hero-wrapper">
                    <span className="news-kicker">{article.kicker}</span>
                    <h1>{article.title}</h1>
                    <h2>{article.sub_title ? article.sub_title : ''}</h2>

                    <div className="news-byline-row">
                        <span>{formatDate(article.date)}</span>
                        <div className="news-share-wrapper">
                            <button
                                ref={heroShareBtnRef}
                                className="news-share-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setHeroShareOpen((v) => !v);
                                }}
                            >
                                <CiShare2 /> Share
                            </button>
                            <ShareMenu
                                open={heroShareOpen}
                                onClose={() => setHeroShareOpen(false)}
                                url={heroShareUrl}
                                title={article.title}
                                anchorRef={heroShareBtnRef}
                                align="right"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="news-article-body">
                {article.content_blocks
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((block, blockIdx) => {
                        if (block.block_type === 'paragraph') {
                            return (
                                <div className="news-paragraph-block" key={block.contentID}>
                                    {block.text.split('\n').map((line, idx) => {
                                        const trimmed = line.trim();
                                        const isBullet = trimmed.startsWith('•') || trimmed.startsWith('-');
                                        const isDropCap = blockIdx === 0 && idx === 0 && !isBullet;

                                        if (isBullet) {
                                            return (
                                                <ul key={idx}>
                                                    <li>{renderWithFormatting(trimmed, `line-${idx}`)}</li>
                                                </ul>
                                            );
                                        }

                                        return (
                                            <p
                                                key={idx}
                                                className={isDropCap ? 'news-dropcap' : undefined}
                                            >
                                                {renderWithFormatting(line, `line-${idx}`, isDropCap)}
                                            </p>
                                        );
                                    })}
                                </div>
                            );
                        }

                        if (block.block_type === 'quote') {
                            return (
                                <div className="news-quote-block" key={block.contentID}>
                                    <BiSolidQuoteLeft className="news-quote-icon" />
                                    <h2>
                                        {block.text}
                                        <BiSolidQuoteRight className="news-quote-icon news-quote-icon-closing" />
                                    </h2>
                                    <p>{block.quote_author}</p>
                                </div>
                            );
                        }

                        if (block.block_type === 'image') {
                            return (
                                <figure className="news-image-block" key={block.contentID}>
                                    <img alt={block.image_title || 'News visual'} src={block.image} />
                                    {(block.image_title || block.image_description) && (
                                        <figcaption>
                                            {block.image_title && <strong>{block.image_title}. </strong>}
                                            {block.image_description}
                                        </figcaption>
                                    )}
                                </figure>
                            );
                        }

                        if (block.block_type === 'video') {
                            const video = getVideoInfo(block.video_url);
                            if (!video) return null;

                            return (
                                <figure className="news-image-block" key={block.contentID}>
                                    {block.image_title && <h3>{block.image_title}</h3>}
                                    <div className="news-video-wrapper">
                                        <iframe
                                            src={video.embedUrl}
                                            title={`${video.type} video player`}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen
                                        />
                                    </div>
                                    {block.image_description && (
                                        <figcaption>{block.image_description}</figcaption>
                                    )}
                                </figure>
                            );
                        }

                        if (block.block_type === 'entity') {
                            return (
                                <div className="news-entity-block" key={block.contentID}>
                                    <div className="news-entity-and-title">
                                        {block.entity_image ? (
                                            <div className="news-entity-image">
                                                <img src={block.entity_image} alt={block.entity} />
                                            </div>
                                        ) : (
                                            <div className="news-entity-avatar">{block.entity.charAt(0)}</div>
                                        )}

                                        <strong>{block.entity}</strong>{' '}
                                    </div>

                                    <p>{renderWithFormatting(block.entity_about, `entity-${block.contentID}`)}</p>

                                    <div className="news-entity-shareables">
                                        {block.entity_website && (
                                            <a
                                                className="entity-shareable-tag"
                                                href={block.entity_website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Website
                                            </a>
                                        )}

                                        {block.entity_email && (
                                            <button
                                                type="button"
                                                className="entity-shareable-tag"
                                                onClick={() => handleCopyEmail(block.entity_email!)}
                                            >
                                                Email
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                        return null;
                    })}

                {/* Contributors */}
                {article.content_blocks.some(
                    (block): block is EntityBlock => block.block_type === 'entity'
                // ) && (
                //         <p className="news-tags">
                //             <strong>Contributors:</strong>{' '}
                //             {article.content_blocks
                //                 .filter((block): block is EntityBlock => block.block_type === 'entity')
                //                 .map((entity) => (
                //                     <span key={entity.contentID} className="news-tag">
                //                         {entity.entity}
                //                     </span>
                //                 ))}
                //         </p>
                    )}
            </div>

            {/* More news */}
            {recommended.length > 0 && (
                <div className="news-more-section">
                    <div className="news-more-inner">
                        <h2>More News</h2>
                        <div className="news-more-grid">
                            {recommended.map((item) => (
                                <MoreNewsCard item={item} key={item.id} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsPost;